/**
 * Watrly Table Component
 * ======================
 * مكون الجدول المتقدم
 */

import React, { useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Table.css';

const Table = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage,
  sortable = true,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  onRowClick,
  pagination = null, // { page, pageSize, total, onPageChange }
  striped = true,
  hoverable = true,
  bordered = false,
  compact = false,
  stickyHeader = false,
  className = '',
  ...props
}) => {
  const { isRTL, t } = useTheme();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // الترتيب
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortable) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig, sortable]);

  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelectionChange?.(data.map((_, index) => index));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (index) => {
    const newSelection = selectedRows.includes(index)
      ? selectedRows.filter((i) => i !== index)
      : [...selectedRows, index];
    onSelectionChange?.(newSelection);
  };

  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  const containerClasses = [
    'watrly-table-container',
    stickyHeader && 'watrly-table-container--sticky',
    className,
  ].filter(Boolean).join(' ');

  const tableClasses = [
    'watrly-table',
    striped && 'watrly-table--striped',
    hoverable && 'watrly-table--hoverable',
    bordered && 'watrly-table--bordered',
    compact && 'watrly-table--compact',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} dir={isRTL ? 'rtl' : 'ltr'} {...props}>
      <div className="watrly-table-wrapper">
        <table className={tableClasses}>
          <thead className="watrly-table__head">
            <tr>
              {selectable && (
                <th className="watrly-table__th watrly-table__th--checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => el && (el.indeterminate = someSelected)}
                    onChange={handleSelectAll}
                    className="watrly-table__checkbox"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={[
                    'watrly-table__th',
                    column.sortable !== false && sortable && 'watrly-table__th--sortable',
                    sortConfig.key === column.key && 'watrly-table__th--sorted',
                    column.align && `watrly-table__th--${column.align}`,
                  ].filter(Boolean).join(' ')}
                  style={{ width: column.width, minWidth: column.minWidth }}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="watrly-table__th-content">
                    <span>{column.title}</span>
                    {column.sortable !== false && sortable && (
                      <span className="watrly-table__sort-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path
                            className={sortConfig.key === column.key && sortConfig.direction === 'asc' ? 'active' : ''}
                            d="M12 5v14M5 12l7-7 7 7"
                            style={{ transform: 'translateY(-3px)' }}
                          />
                          <path
                            className={sortConfig.key === column.key && sortConfig.direction === 'desc' ? 'active' : ''}
                            d="M12 19V5M19 12l-7 7-7-7"
                            style={{ transform: 'translateY(3px)' }}
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="watrly-table__body">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="watrly-table__loading">
                  <div className="watrly-table__spinner">
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                    </svg>
                  </div>
                  <span>{t?.('table.loading') || 'جاري التحميل...'}</span>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="watrly-table__empty">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="1" />
                    <path d="M9 12h6M9 16h6" />
                  </svg>
                  <span>{emptyMessage || t?.('table.noRecords') || 'لا توجد بيانات'}</span>
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  className={[
                    'watrly-table__row',
                    selectedRows.includes(rowIndex) && 'watrly-table__row--selected',
                    onRowClick && 'watrly-table__row--clickable',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onRowClick?.(row, rowIndex)}
                >
                  {selectable && (
                    <td className="watrly-table__td watrly-table__td--checkbox">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(rowIndex)}
                        onChange={() => handleSelectRow(rowIndex)}
                        onClick={(e) => e.stopPropagation()}
                        className="watrly-table__checkbox"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={[
                        'watrly-table__td',
                        column.align && `watrly-table__td--${column.align}`,
                      ].filter(Boolean).join(' ')}
                    >
                      {column.render
                        ? column.render(row[column.key], row, rowIndex)
                        : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="watrly-table__pagination">
          <div className="watrly-table__pagination-info">
            {t?.('table.showing') || 'عرض'}{' '}
            {Math.min((pagination.page - 1) * pagination.pageSize + 1, pagination.total)}-
            {Math.min(pagination.page * pagination.pageSize, pagination.total)}{' '}
            {t?.('general.of') || 'من'} {pagination.total}
          </div>

          <div className="watrly-table__pagination-controls">
            <button
              className="watrly-table__pagination-btn"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange?.(pagination.page - 1)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <span className="watrly-table__pagination-pages">
              {pagination.page} / {Math.ceil(pagination.total / pagination.pageSize)}
            </span>

            <button
              className="watrly-table__pagination-btn"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
              onClick={() => pagination.onPageChange?.(pagination.page + 1)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
